import React, { useState, useEffect } from "react";
import Storefront from "../../components/Storefront/Storefront";
import Countdown from "../../components/Storefront/Countdown";
import { useParams } from "react-router-dom";
import { FundraiserResponse } from "../../Interfaces/Fundraiser";
import { FundraiserParticipantShop } from "../../Interfaces/FundraiserRole";
import { format, toZonedTime } from "date-fns-tz";
import "./ParticipantShop.css";
import ParticipantProfileUI from "../../components/Storefront/ParticipantProfileUI";
import whiteHeartShirt from "../../images/white_shirt_heart.png";
import whiteSouthwestShirt from "../../images/WhiteShirt_Southwest.png";
import DonationUI from "../../components/Storefront/Donation";

import config from "../../config";
import {
  DEFAULT_BACKGROUND_COLOR,
  formatPossessive,
  isLightBackground,
} from "../../CommonFunctions";
import { useCart } from "../../contexts/Cart/CartContext";
import { CiShoppingCart } from "react-icons/ci";
import EnterfundraiserID from "../EnterFundraiserID";
import FreeShippingDisplay from "../../components/Storefront/FreeShippingDisplay";

const BASE_API = config.API_BASE_URL;

const FloatingButton = () => {
  const { toggleDrawer } = useCart();

  return (
    <button
      className="participant-shop-floating-button"
      onClick={() => toggleDrawer()}
    >
      <CiShoppingCart fontSize={50} />
    </button>
  );
};

const OrgDisplay: React.FC<{ fundraiserResponse: FundraiserResponse }> = ({
  fundraiserResponse,
}) => {
  const orgDisplayStyles = {
    "--org-display-bg": fundraiserResponse.primary_color,
    "--org-display-text-color": isLightBackground(
      fundraiserResponse?.primary_color || DEFAULT_BACKGROUND_COLOR
    )
      ? "#000000"
      : "#FFFFFF",
  } as React.CSSProperties;

  return (
    <div className="org-display-container" style={orgDisplayStyles}>
      <div className="org-display-name">{fundraiserResponse.org_name}</div>
      <div className="org-display-image-container">
        <img
          src={`${config.BACKEND_BASE_IMAGE_LOCATION}${fundraiserResponse.org_image}`}
          alt={fundraiserResponse.org_name}
          className="org-display-organization-image"
        />
      </div>
    </div>
  );
};

const OurGoal: React.FC<{ fundraiserResponse: FundraiserResponse }> = ({
  fundraiserResponse,
}) => {
  // Convert newlines in the message to <br /> tags
  const renderMessageWithNewLines = (message: string) => {
    return message.split("\n").map((line, index) => <p key={index}>{line}</p>);
  };

  return (
    <div className="our-goal-container">
      <div className="our-goal-header">Our Goal</div>
      <div className="our-goal-content">
        <div className="our-goal-message">
          {renderMessageWithNewLines(
            fundraiserResponse.fundraiser_purpose_message
          )}
        </div>
      </div>
    </div>
  );
};

const PersonalizedShopBanner = () => {
  return (
    <div className="shop-banner">
      <div className="personalized-shop-text">
        Shop personalized tees for every team
      </div>
      <div className="personalized-shop-image-container">
        <img src={whiteHeartShirt} alt="White Heart Shirt" />
        <img src={whiteSouthwestShirt} alt="White Southwest Shirt" />
      </div>
    </div>
  );
};

const ParticipantShop = () => {
  const [fundraiserObject, setFundraiserObject] =
    useState<FundraiserResponse | null>(null);
  const [participantObject, setParticipantObject] =
    useState<FundraiserParticipantShop | null>(null);
  const { participantID } = useParams<{ participantID: string }>();
  const [invalidPartCode, setInvalidPartCode] = useState(false);
  const { freeShippingThreshold } = useCart();

  // Sample participant data for testing
  const getParticipantObject = async (participantID: string) => {
    const participantIDAsNum: number = Number(participantID.substring(4));

    try {
      const response = await fetch(
        `${BASE_API}fundraiser-role/store-info/${participantIDAsNum}/`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data: FundraiserParticipantShop = await response.json();
      setParticipantObject(data);
      if (data["participant_code"] !== participantID) {
        setInvalidPartCode(true);
      }
    } catch (error) {
      console.error("Error fetching participant data:", error);
    }
  };

  // Fetch fundraiser object
  const fetchFundraiserObject = async (fundraiserId: number) => {
    try {
      const response = await fetch(
        `${BASE_API}fundraiser/${fundraiserId}/detailed`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data: FundraiserResponse = await response.json();
      setFundraiserObject(data);
    } catch (error) {
      console.error("Error fetching fundraiser data:", error);
    }
  };

  useEffect(() => {
    if (participantID) {
      getParticipantObject(participantID);
    }
  }, [participantID]);

  useEffect(() => {
    if (participantObject) {
      fetchFundraiserObject(participantObject.fundraiser);
      if (!invalidPartCode) {
        document.title = `${formatPossessive(
          participantObject.full_name
        )} Shop`;
      }
    }
  }, [participantObject]);

  useEffect(() => {
    document.title = "Participant Shop";
  }, []);

  if (!participantObject) {
    return <div>Loading participant data...</div>;
  }

  if (!fundraiserObject) {
    return <div>Loading fundraiser data...</div>;
  }

  if (invalidPartCode) {
    document.title = "Participant Shop";
    return (
      <div>
        <div className="part-shop-error-code-mismatch-title">
          Error! Participant code mismatch
        </div>
        <EnterfundraiserID />
      </div>
    );
  }

  const timeZone = "America/Los_Angeles";
  const endDateInPST = toZonedTime(
    `${fundraiserObject.end_date}T23:59:00-08:00`,
    timeZone
  );
  const endDateStringWithTime = format(
    endDateInPST,
    "yyyy-MM-dd'T'HH:mm:ssXXX"
  );
  const targetTimestamp = Date.parse(endDateStringWithTime);
  const isTimeOver = Date.now() >= targetTimestamp;

  return (
    <div>
      <OrgDisplay fundraiserResponse={fundraiserObject} />
      <ParticipantProfileUI
        participantShop={participantObject}
        fundraiser={fundraiserObject}
      />
      <Countdown
        targetTimestamp={targetTimestamp}
        fundraiser={fundraiserObject}
      />
      <OurGoal fundraiserResponse={fundraiserObject} />
      <FreeShippingDisplay freeThreshold={freeShippingThreshold} />
      <PersonalizedShopBanner />
      {!isTimeOver && <DonationUI participantShop={participantObject} />}
      {isTimeOver ? (
        <h1 className="text-center mt-4">
          This fundraiser has ended and is no longer accepting orders
        </h1>
      ) : (
        <Storefront
          fundraiserId={participantObject.fundraiser}
          participant={participantObject}
        />
      )}
      <FloatingButton />
    </div>
  );
};

export default ParticipantShop;
